// src/components/PaymentCallback.jsx - FIXED VERSION
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [orderData, setOrderData] = useState(null);
  const hasProcessed = useRef(false); // Prevent multiple processing
  const retryCount = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    const processPayment = async () => {
      if (hasProcessed.current) return;

      const reference = searchParams.get('reference') || searchParams.get('trxref');
      
      console.log('🔄 Payment callback received, reference:', reference);

      if (!reference) {
        console.error('❌ No payment reference found');
        setStatus('error');
        toast.error('No payment reference found in URL');
        return;
      }

      hasProcessed.current = true;

      try {
        // Get authentication token
        const token = localStorage.getItem('token') || localStorage.getItem('bloomsoko-token');
        if (!token) {
          throw new Error('Please login to complete payment verification');
        }

        console.log('🔍 Verifying payment with reference:', reference);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/paystack/verify/${reference}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('📡 Verification response status:', response.status);
        const result = await response.json();
        console.log('📦 Verification result:', result);

        if (response.ok && result.success) {
          setStatus('success');
          setOrderData(result.data);
          toast.success('Payment verified successfully! 🎉');
          
          // ✅ FIXED: Clear cart AFTER successful verification
          setTimeout(() => {
            localStorage.removeItem('pendingCartClear');
            navigate('/order-success', {
              state: {
                orderId: result.data.order.orderNumber,
                total: result.data.order.totalAmount,
                recipientEmail: result.data.order.recipient.email,
                order: result.data.order
              }
            });
          }, 2000);
          
        } else if (result.code === 'NETWORK_ERROR' && retryCount.current < maxRetries) {
          // ✅ FIXED: Retry on network errors
          retryCount.current++;
          console.log(`🔄 Retrying verification (${retryCount.current}/${maxRetries})...`);
          hasProcessed.current = false;
          setTimeout(processPayment, 2000 * retryCount.current); // Exponential backoff
          
        } else {
          console.error('❌ Payment verification failed:', result.message);
          setStatus('error');
          toast.error(result.message || 'Payment verification failed');
          
          // Navigate to orders page with error state
          setTimeout(() => {
            navigate('/orders', { 
              state: { 
                paymentError: true,
                reference: reference 
              } 
            });
          }, 3000);
        }
      } catch (error) {
        console.error('❌ Payment verification error:', error);
        setStatus('error');
        toast.error(`Verification error: ${error.message}`);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <h2 style={{ color: '#FFA000', marginBottom: '1rem' }}>Verifying Payment...</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Please wait while we confirm your payment.
              {retryCount.current > 0 && ` (Retry ${retryCount.current}/${maxRetries})`}
            </p>
            <div style={{
              margin: '0 auto',
              width: '50px',
              height: '50px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #2E7D32',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        );

      case 'success':
        return (
          <div>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: '#2E7D32', marginBottom: '1rem' }}>Payment Successful! 🎉</h2>
            {orderData && (
              <div style={{
                background: '#F1F8E9',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                textAlign: 'left'
              }}>
                <p><strong>Order Number:</strong> {orderData.order.orderNumber}</p>
                <p><strong>Amount:</strong> KSh {orderData.order.totalAmount?.toLocaleString()}</p>
                <p><strong>Status:</strong> {orderData.order.status}</p>
              </div>
            )}
            <p style={{ color: '#666' }}>
              Redirecting to order details...
            </p>
          </div>
        );

      case 'error':
        return (
          <div>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ color: '#F44336', marginBottom: '1rem' }}>Payment Verification Issue</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              There was an issue verifying your payment. Don't worry, your payment may still have been successful.
              Please check your orders page or contact support if you don't see your order within 24 hours.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => navigate('/orders')}
                style={{
                  background: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                View Orders
              </button>
              <button 
                onClick={() => navigate('/')}
                style={{
                  background: '#FFC107',
                  color: 'black',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Return Home
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '2rem auto',
      padding: '2rem',
      textAlign: 'center',
      background: '#FFFFFF',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      {renderContent()}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentCallback;